import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useWorkspace } from '@/hooks/useWorkspace';
import { Building2, Search, Star, Clock, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNavigate } from 'react-router-dom';
import { Company } from '@/types';
import { logger } from '@/utils/logger';

interface CompanyWithMetadata extends Company {
  isFavorite?: boolean;
  lastAccessedAt?: string;
}

interface CompanySwitcherProps {
  disabled?: boolean;
  onSwitch?: (companyId: string) => void;
}

// Constants for tracking preferences
const RECENT_COMPANIES_KEY = 'marketing-mary-recent-companies';
const FAVORITE_COMPANIES_KEY = 'marketing-mary-favorite-companies';
const MAX_RECENT_COMPANIES = 5;

export const SearchableCompanySwitcher: React.FC<CompanySwitcherProps> = ({ 
  disabled = false,
  onSwitch 
}) => {
  const { currentCompany, companies, switchCompany } = useWorkspace();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Load user preferences from localStorage
  const loadPreferences = useCallback(() => {
    const recentIds = JSON.parse(
      localStorage.getItem(RECENT_COMPANIES_KEY) || '[]'
    ) as string[];
    const favoriteIds = JSON.parse(
      localStorage.getItem(FAVORITE_COMPANIES_KEY) || '[]'
    ) as string[];
    
    return { recentIds, favoriteIds };
  }, []);

  // Save preferences to localStorage
  const savePreferences = useCallback((
    recentIds: string[], 
    favoriteIds: string[]
  ) => {
    localStorage.setItem(RECENT_COMPANIES_KEY, JSON.stringify(recentIds));
    localStorage.setItem(FAVORITE_COMPANIES_KEY, JSON.stringify(favoriteIds));
  }, []);

  // Update recent companies when switching
  const updateRecentCompanies = useCallback((companyId: string) => {
    const { recentIds } = loadPreferences();
    const newRecentIds = [
      companyId,
      ...recentIds.filter(id => id !== companyId)
    ].slice(0, MAX_RECENT_COMPANIES);
    
    const { favoriteIds } = loadPreferences();
    savePreferences(newRecentIds, favoriteIds);
  }, [loadPreferences, savePreferences]);

  // Toggle favorite status
  const toggleFavorite = useCallback((companyId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const { recentIds, favoriteIds } = loadPreferences();
    const newFavoriteIds = favoriteIds.includes(companyId)
      ? favoriteIds.filter(id => id !== companyId)
      : [...favoriteIds, companyId];
    
    savePreferences(recentIds, newFavoriteIds);
    // Force re-render
    setSearch(prev => prev);
  }, [loadPreferences, savePreferences]);

  // Process companies with metadata
  const companiesWithMetadata = useMemo(() => {
    const { recentIds, favoriteIds } = loadPreferences();
    
    return companies.map(company => ({
      ...company,
      isFavorite: favoriteIds.includes(company.id),
      lastAccessedAt: recentIds.includes(company.id) 
        ? new Date(Date.now() - recentIds.indexOf(company.id) * 1000).toISOString()
        : undefined
    })) as CompanyWithMetadata[];
  }, [companies, loadPreferences]);  

  // Filter and sort companies
  const filteredCompanies = useMemo(() => {
    const searchLower = search.toLowerCase();
    
    // Filter by search
    let filtered = companiesWithMetadata;
    if (searchLower) {
      filtered = companiesWithMetadata.filter(company => 
        company.name.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort by: current company first, then favorites, then recent, then alphabetical
    return filtered.sort((a, b) => {
      // Current company always first
      if (a.id === currentCompany?.id) return -1;
      if (b.id === currentCompany?.id) return 1;
      
      // Favorites next
      if (a.isFavorite && !b.isFavorite) return -1;
      if (!a.isFavorite && b.isFavorite) return 1;
      
      // Recent companies after favorites
      if (a.lastAccessedAt && !b.lastAccessedAt) return -1;
      if (!a.lastAccessedAt && b.lastAccessedAt) return 1;
      if (a.lastAccessedAt && b.lastAccessedAt) {
        return b.lastAccessedAt.localeCompare(a.lastAccessedAt);
      }
      
      // Alphabetical for the rest
      return a.name.localeCompare(b.name);
    });
  }, [companiesWithMetadata, search, currentCompany]);

  // Group companies for display
  const groupedCompanies = useMemo(() => {
    const groups: { 
      favorites: CompanyWithMetadata[]; 
      recent: CompanyWithMetadata[]; 
      all: CompanyWithMetadata[] 
    } = {
      favorites: [],
      recent: [],
      all: []
    };
    
    filteredCompanies.forEach(company => {
      if (company.isFavorite) {
        groups.favorites.push(company);
      } else if (company.lastAccessedAt && groups.recent.length < MAX_RECENT_COMPANIES) {
        groups.recent.push(company);
      } else {
        groups.all.push(company);
      }
    });
    
    return groups;
  }, [filteredCompanies]);

  // Calculate total items for keyboard navigation
  const totalItems = filteredCompanies.length;

  // Handle company selection
  const handleSelect = useCallback(async (company: CompanyWithMetadata) => {
    if (company.id === currentCompany?.id) {
      setOpen(false);
      return;
    }
    
    setIsLoading(true);
    updateRecentCompanies(company.id);
    
    try {
      // Call custom handler if provided
      if (onSwitch) {
        await onSwitch(company.id);
      } else {
        // Default behavior
        switchCompany(company.id);
        navigate(`/w/${company.slug}/dashboard`);
      }
    } catch (error) {
      logger.error('Failed to switch company', error);
    } finally {
      setIsLoading(false);
      setOpen(false);
    }
  }, [currentCompany, updateRecentCompanies, onSwitch, switchCompany, navigate]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!open) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % totalItems);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + totalItems) % totalItems);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredCompanies[selectedIndex]) {
          handleSelect(filteredCompanies[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        break;
    }
  }, [open, totalItems, selectedIndex, filteredCompanies, handleSelect]);

  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  // Focus search input when opened
  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const items = listRef.current.querySelectorAll('[data-company-item]');
      const selectedItem = items[selectedIndex] as HTMLElement;
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  if (!currentCompany) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2"
          disabled={disabled || isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" />
              <span className="font-medium">Switching...</span>
            </>
          ) : (
            <>
              <span className="font-medium">{currentCompany.name}</span>
              {companies.length > 1 && (
                <span className="text-xs text-muted-foreground ml-1">
                  ({companies.length})
                </span>
              )}
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="start"
        onKeyDown={handleKeyDown}
      >
        {/* Search Header */}
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              placeholder="Search companies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-10"
            />
            {search && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                onClick={() => setSearch('')}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>

        {/* Companies List */}
        <ScrollArea className="h-[400px]">
          <div ref={listRef} className="py-1">
            {filteredCompanies.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                No companies found
              </div>
            ) : (
              <>
                {/* Favorites Section */}
                {groupedCompanies.favorites.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground">
                      Favorites
                    </div>
                    {groupedCompanies.favorites.map((company, index) => (
                      <CompanyItem
                        key={company.id}
                        company={company}
                        isSelected={index === selectedIndex}
                        isCurrent={company.id === currentCompany.id}
                        onSelect={() => handleSelect(company)}
                        onToggleFavorite={(e) => toggleFavorite(company.id, e)}
                      />
                    ))}
                  </>
                )}

                {/* Recent Section */}
                {groupedCompanies.recent.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground">
                      Recent
                    </div>
                    {groupedCompanies.recent.map((company, index) => (
                      <CompanyItem
                        key={company.id}
                        company={company}
                        isSelected={
                          index + groupedCompanies.favorites.length === selectedIndex
                        }
                        isCurrent={company.id === currentCompany.id}
                        onSelect={() => handleSelect(company)}
                        onToggleFavorite={(e) => toggleFavorite(company.id, e)}
                      />
                    ))}
                  </>
                )}

                {/* All Companies Section */}
                {groupedCompanies.all.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground">
                      All Companies
                    </div>
                    {groupedCompanies.all.map((company, index) => (
                      <CompanyItem
                        key={company.id}
                        company={company}
                        isSelected={
                          index +
                          groupedCompanies.favorites.length +
                          groupedCompanies.recent.length ===
                          selectedIndex
                        }
                        isCurrent={company.id === currentCompany.id}
                        onSelect={() => handleSelect(company)}
                        onToggleFavorite={(e) => toggleFavorite(company.id, e)}
                      />
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="p-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-muted-foreground"
            onClick={() => {
              setOpen(false);
              navigate('/companies');
            }}
          >
            Manage Companies
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Company Item Component
interface CompanyItemProps {
  company: CompanyWithMetadata;
  isSelected: boolean;
  isCurrent: boolean;
  onSelect: () => void;
  onToggleFavorite: (e: React.MouseEvent) => void;
}

const CompanyItem: React.FC<CompanyItemProps> = ({
  company,
  isSelected,
  isCurrent,
  onSelect,
  onToggleFavorite,
}) => {
  return (
    <div
      data-company-item
      className={cn(
        'flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent',
        isSelected && 'bg-accent',
        isCurrent && 'font-medium'
      )}
      onClick={onSelect}
    >
      <Building2 className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <div className="truncate">{company.name}</div>
        {company.lastAccessedAt && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>Recently used</span>
          </div>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          'h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity',
          company.isFavorite && 'opacity-100'
        )}
        onClick={onToggleFavorite}
      >
        <Star
          className={cn(
            'h-3.5 w-3.5',
            company.isFavorite
              ? 'fill-yellow-500 text-yellow-500'
              : 'text-muted-foreground'
          )}
        />
      </Button>
    </div>
  );
};